/**
 * CountryStackedTable · Module C 堆叠条视图（参考 Figma 3277-19886）
 *
 * 表头结构（与 figma 一致）：
 * - 国家
 * - 游戏列表：每个游戏一个独立的 HoverSortCommonSimple（含 cover icon + 游戏名 + ▾ Asc/Des/Default）
 * - 总计（仅本视图保留）
 *
 * 行结构：
 * - 国家列：flag + 国名（hover 时整行显示游戏汇总 tooltip）
 * - 中部跨列：一根多色水平堆叠条
 *   - 堆叠条下方分散显示 [color dot] + DBO_V.reviews(value)
 * - 总计列：DBO_V.reviews(total)
 *
 * ⚠️ 美国固定第一行 by mock；列顺序由首行（美国）games 顺序锁定
 */
import React, { useMemo } from 'react';
import TableCommonHook from '@op/components/TableCommonHook';
import RegionCountryItem from '@op/components/Snippets/RegionCountryItem';
import RenderImg from '@op/components/RenderImg';
import { DefaultImg } from '@op/constants/defaultImg';
import { Bubble } from 'tea-component';
import MouseTooltip from './MouseTooltip';
import { DBO_V } from '@op/utils/format';
import { useGameColorContext } from '../../context/GameColorContext';
import useIntls from '@opm/hooks/useIntls';
import { ICountryRowItem } from '../../types/country';
import { TGameMap } from '../../hooks/useGameMap';
import {
  ICountrySortByState,
  TOTAL_SORT_KEY,
} from '../../hooks/useCountryCompare';
import HoverSortCommonSimple from '@op/components/HoverSortCommonSimple';
import GameListSortHeader from './GameListSortHeader';
import './index.scss';

export interface IStackedPageInfo {
  page: number;
  page_size: number;
}

export interface ICountryStackedTableProps {
  list: ICountryRowItem[];
  gameMap: TGameMap;
  /** 列顺序（来自 list[0].games，美国行锁定） */
  gameOrder: string[];
  sortBy: ICountrySortByState;
  setSortBy: (s: ICountrySortByState) => void;
  loading?: boolean;
  /** 前端分页 state（由父级共享 stacked / columns 视图） */
  pageInfo?: IStackedPageInfo;
  setPageInfo?: (info: IStackedPageInfo) => void;
}

const ROW_HEIGHT = 84; // 堆叠条 + 数值标签
const HEADER_HEIGHT = 56;
const MIN_HEIGHT = 320;

interface ISegmentProps {
  value: number;
  total: number;
  color: string;
}

/** 堆叠条段（仅渲染一段） */
function Segment({ value, total, color }: ISegmentProps) {
  const percent = DBO_V.percentage(value, total, 2);
  return (
    <span
      className="dbom-country-stacked__seg"
      style={{ width: `${percent}`, backgroundColor: color }}
    />
  );
}

/** 国家行 hover tooltip：所有游戏列表（参考 figma 黑色浮窗） */
function CountryRowTooltipContent({
  row,
  gameMap,
  gameOrder,
  gameColor,
}: {
  row: ICountryRowItem;
  gameMap: TGameMap;
  gameOrder: string[];
  gameColor: Record<string, string>;
}) {
  const { formatMessage } = useIntls();
  return (
    <div className="dbom-country-stacked__tip">
      <div className="dbom-country-stacked__tip-head">
        <RegionCountryItem
          market={row.country_code}
          iconStyle={{ width: 18, height: 14 }}
        />
        <span className="dbom-country-stacked__tip-title">{formatMessage('Active Ads')}</span>
      </div>
      <ul className="dbom-country-stacked__tip-list">
        {gameOrder.map((gameId) => {
          const found = row.games.find((g) => g.game_id === gameId);
          const value = found?.value ?? 0;
          if (value <= 0) return null;
          const game = gameMap[gameId];
          const percent = DBO_V.percentage(value, row.total, 2);
          return (
            <li key={gameId} className="dbom-country-stacked__tip-item">
              <span
                className="dbom-country-stacked__dot"
                style={{ backgroundColor: gameColor[gameId] }}
              />
              <RenderImg
                style={{ width: 16, height: 16, borderRadius: 4 }}
                coverImage={game?.cover || ''}
                defaultImage={DefaultImg.GAME}
                mode="image"
              />
              <span className="dbom-country-stacked__tip-name" title={game?.game_name || gameId}>
                {game?.game_name || gameId}
              </span>
              <span className="dbom-country-stacked__tip-value">
                {DBO_V.reviews(value)}({percent})
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const DEFAULT_PAGE_INFO: IStackedPageInfo = { page: 1, page_size: 10 };

export default function CountryStackedTable({
  list,
  gameMap,
  gameOrder,
  sortBy,
  setSortBy,
  loading,
  pageInfo: pageInfoProp,
  setPageInfo: setPageInfoProp,
}: ICountryStackedTableProps) {
  const { intl } = useIntls();

  const labels = useMemo(
    () => ({
      country: intl('Country'),
      total: intl('Total'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl],
  );

  /** 从 Context 获取全局游戏颜色映射 */
  const { gameColorMap: gameColor } = useGameColorContext();



  /**
   * 数值标签渲染顺序：
   * - 当前选中排序的游戏置首（sortBy.by；TOTAL_SORT_KEY 不算）
   * - 其余按 gameOrder 顺序（已排除置首的那个）
   */
  const valuesGameOrder = useMemo(() => {
    const selectedGameId =
      sortBy.by && sortBy.by !== TOTAL_SORT_KEY
        ? sortBy.by
        : '';
    if (!selectedGameId) return gameOrder;
    if (!gameOrder.includes(selectedGameId)) return gameOrder;
    return [
      selectedGameId,
      ...gameOrder.filter(id => id !== selectedGameId),
    ];
  }, [gameOrder, sortBy.by]);

  /** 表格列：国家 / 每个游戏一列（仅作表头排序触发；body 渲染由 stacked 行承担）/ 总计 */
  const columns = useMemo(() => {
    const cols: any[] = [
      {
        key: 'country',
        header: labels.country,
        width: 180,
        fixed: 'left' as const,
        render: (row: ICountryRowItem) => (
          <Bubble
            dark
            placement="bottom"
            content={
              <CountryRowTooltipContent
                row={row}
                gameMap={gameMap}
                gameOrder={gameOrder}
                gameColor={gameColor}
              />
            }
          >
            <span style={{ display: 'inline-flex' }}>
              <RegionCountryItem
                market={row.country_code}
                iconStyle={{ width: 22, height: 16 }}
              />
            </span>
          </Bubble>
        ),
      },
    ];
    // 单个 Stacked 列承载堆叠条 + 表头是统一的 GameListSortHeader
    cols.push({
      key: 'stacked',
      header: (
        <GameListSortHeader
          gameOrder={gameOrder}
          gameMap={gameMap}
          sortBy={sortBy}
          setSortBy={setSortBy}
          gameColor={gameColor}
        />
      ),
      render: (row: ICountryRowItem) => (
        <MouseTooltip
          estimatedWidth={280}
          estimatedHeight={Math.min(420, 36 + gameOrder.length * 24)}
          content={
            <CountryRowTooltipContent
              row={row}
              gameMap={gameMap}
              gameOrder={gameOrder}
              gameColor={gameColor}
            />
          }
        >
          <div className="dbom-country-stacked__cell">
            <div className="dbom-country-stacked__bar">
              {valuesGameOrder.map((gameId) => {
                const found = row.games.find((g) => g.game_id === gameId);
                return (
                  <Segment
                    key={gameId}
                    value={found?.value ?? 0}
                    total={row.total}
                    color={gameColor[gameId]}
                  />
                );
              })}
            </div>

            <div className="dbom-country-stacked__values">
              {valuesGameOrder.map((gameId) => {
                const found = row.games.find((g) => g.game_id === gameId);
                const game = gameMap[gameId];
                if (!found || found.value <= 0) return null;
                return (
                  <span
                    key={gameId}
                    className="dbom-country-stacked__val-item"
                  >
                    <span
                      className="dbom-country-stacked__dot"
                      style={{ backgroundColor: gameColor[gameId] }}
                    />
                    <RenderImg
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 4,
                        marginRight: 6,
                        flexShrink: 0,
                      }}
                      coverImage={game?.cover || ''}
                      defaultImage={DefaultImg.GAME}
                      mode="image"
                    />
                    {DBO_V.reviews(found.value)}
                  </span>
                );
              })}
            </div>
          </div>
        </MouseTooltip>
      ),
    });

    /** Total 列：用 HoverSortCommonSimple 提供 Asc/Des/Default 三选项 */
    cols.push({
      key: 'total',
      header: (
        <HoverSortCommonSimple
          sortKey={TOTAL_SORT_KEY}
          sort={{
            by: sortBy.by || '',
            order: (sortBy.order || 'desc') as 'asc' | 'desc',
          }}
          sortChange={(next) => {
            if (!next?.by) {
              setSortBy({ order: "desc", by: "" });
              return;
            }
            setSortBy({ by: TOTAL_SORT_KEY, order: next.order || "desc" });
          }}
          sortLabel={intl('Sort By')}
          dropdownProps={{
            boxClassName: 'dbom-game-sort-header'
          }}
        >
          {labels.total}
        </HoverSortCommonSimple>
      ),
      align: 'right' as const,
      width: 140,
      fixed: 'right' as const,
      render: (row: ICountryRowItem) =>
        row.total > 0 ? DBO_V.reviews(row.total) : '-',
    });

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labels, gameOrder, valuesGameOrder, gameMap, gameColor, sortBy]);

  /** 前端分页：受控（父传）或本地兜底 */
  const [localPageInfo, setLocalPageInfo] =
    React.useState<IStackedPageInfo>(DEFAULT_PAGE_INFO);
  const pageInfo = pageInfoProp || localPageInfo;
  const setPageInfo = setPageInfoProp || setLocalPageInfo;

  /** 当前页切片（前端分页） */
  const pagedList = useMemo(() => {
    const start = (pageInfo.page - 1) * pageInfo.page_size;
    return list.slice(start, start + pageInfo.page_size);
  }, [list, pageInfo]);

  const tableHeight = useMemo(
    () =>
      Math.max(MIN_HEIGHT, HEADER_HEIGHT + pagedList.length * ROW_HEIGHT),
    [pagedList.length],
  );

  const recordKey = useMemo(() => (r: ICountryRowItem) => r.country_code, []);

  return (
    <TableCommonHook
      className="dbom-country-stacked-table"
      records={pagedList}
      columns={columns}
      recordKey={recordKey}
      total={list.length}
      pageInfo={pageInfo}
      setPageInfo={setPageInfo}
      defaultPageInfo={DEFAULT_PAGE_INFO}
      height={tableHeight}
      loading={loading}
    />
  );
}
